import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye } from "react-icons/fi";
import { Link } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { toast } from "react-toastify";

const OrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [myPermissions, setMyPermissions] = useState([]);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const API = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("admin_token");

  // ✅ Siparişleri çek
  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

const filtered = (res.data || []).filter(
  (o) => o.status === "cancelled"
);
      setOrders(filtered);
    } catch (error) {
      console.error("❌ Sipariş verileri alınamadı:", error);
      toast.error(getText("ORDERS_FETCH_ERROR"));
    }
  };

  // ✅ Kullanıcı izinlerini çek
  const fetchPermissions = async () => {
    try {
      const resp = await axios.get(`${API}/api/admin/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMyPermissions(resp.data.permissions || []);
    } catch (err) {
      console.error("Permission alınamadı:", err);
      setMyPermissions([]);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchPermissions();
  }, []);

  // ✅ İzin kaydı – orders module
  const orderModulePerm = myPermissions.find(
    (p) => p.module_key === "static_orders",
  );

  const canView =
    orderModulePerm?.view_permission || orderModulePerm?.full_access;

  // ✅ Tablo kolonları
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: () => getText("TABLE_ID_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 80,
      },
      {
        accessorKey: "merchant_oid",
        header: () => getText("TABLE_ORDER_NO_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 160,
      },
      {
        accessorKey: "customer_fullname",
        header: () => getText("TABLE_CUSTOMER_NAME_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 200,
      },
      {
        accessorKey: "personnel_fullname",
        header: () => getText("TABLE_PERSONNEL_NAME_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 180,
      },
      {
        accessorKey: "payment_method",
        header: () => getText("TABLE_PAID_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 120,
        cell: (info) => {
          const val = info.getValue();

          const map = {
            cash: getText("PAYMENT_CASH"),
            card: getText("PAYMENT_CARD"),
            transfer: getText("PAYMENT_TRANSFER"),
            eft: getText("PAYMENT_EFT"),
            pos: getText("PAYMENT_POS"),
          };

          return <span>{map[val] || getText("PAYMENT_CASH")}</span>;
        },
      },
      {
        accessorKey: "total_amount",
        header: () => getText("TABLE_AMOUNT_LABEL"),

        meta: { headerClassName: "text-end", className: "text-end" },
        size: 120,
        cell: (info) => {
          const row = info.row.original;
          const symbol = row.currency_symbol;
          const value = Number(info.getValue() || 0);

          return (
            <div className="text-end">
              {value.toLocaleString(language === "en" ? "en-US" : "tr-TR")}{" "}
              {symbol}
            </div>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: () => getText("TABLE_DATE_LABEL"),
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 140,
        cell: (info) => {
          const d = new Date(info.getValue());
          const formatted = d.toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return <div className="text-end">{formatted}</div>;
        },
      },
      {
        accessorKey: "status",
        header: () => getText("TABLE_STATUS_LABEL"),
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 140,
        cell: (info) => {
          const status = info.getValue();

          const map = {
            pending: {
              label: getText("ORDER_STEP_PENDING"),
              color: "secondary",
            },
            confirmed: {
              label: getText("ORDER_STEP_PENDING"),
              color: "info",
            },
            paid: {
              label: getText("ORDER_STATUS_PENDING"),
              color: "success",
            },
            preparing: {
              label: getText("ORDER_STATUS_PENDING"),
              color: "warning",
            },
            shipped: {
              label: getText("ORDER_STATUS_SHIPPED"),
              color: "primary",
            },
            delivered: {
              label: getText("ORDER_STATUS_DELIVERED"),
              color: "success",
            },

            cancelled: {
              label: getText("ORDER_STATUS_CANCELLED"),
              color: "danger",
            },

            partially_cancelled: {
              label: getText("ORDER_STATUS_PARTIALLY_CANCELLED"),
              color: "warning",
            },

            refunded: {
              label: getText("ORDER_STATUS_REFUNDED"),
              color: "dark",
            },

            // 🔥 YENİ EKLENENLER
            partially_returned: {
              label: getText("ORDER_STATUS_PARTIALLY_RETURNED"),
              color: "info",
            },

            fully_returned: {
              label: getText("ORDER_STATUS_FULLY_RETURNED"),
              color: "dark",
            },
          };

          const s = map[status] || { label: status, color: "secondary" };

          return (
            <div className="d-flex justify-content-end">
              <span className={`badge bg-${s.color}`}>{s.label}</span>
            </div>
          );
        },
      },

      {
        accessorKey: "actions",
        header: () => getText("TABLE_ACTIONS_LABEL"),
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 160,
        cell: (info) => {
          const id = info.row.original.id;

          return (
            <div
              className="hstack gap-2 justify-content-end"
              style={{ paddingRight: "20px" }}
            >
              <Link
                to={`/order/view/${id}`}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={getText("VIEW")}
              >
                <FiEye />
              </Link>
            </div>
          );
        },
      },
    ],
    [orders, getText, canView, translations],
  );

  return <Table data={orders} columns={columns} />;
};

export default OrdersTable;
